import Downshift, { DownshiftState, StateChangeOptions } from 'downshift'
import { exists } from 'helpers/exists'
import { useHydrationDone } from 'helpers/hooks/useHydrationDone'
import React, { Fragment, ReactNode, memo, useCallback, useMemo } from 'react'
import styled, { css } from 'styled-components'
import {
  complementaryColor,
  inactiveColor,
  primaryColor,
} from 'uiComponents/constants/colors'
import {
  fontFamilyPrimary,
  fsXS,
  fwMediumBold,
} from 'uiComponents/constants/typography'
import SingleLabelOption from 'uiComponents/molecules/Dropdown/DropdownOptions/SingleLabelOption'
import DropdownBox from './DropdownBox'
import DropdownButton from './DropdownButton'
import OptionContainer from './DropdownOptions/OptionContainer'
import MultiSelectLabel from './MultiSelectLabel'
import { BaseDropdownOptionType } from './types'

const MemoOptionContainer = memo(OptionContainer)

export interface IDropdownButtonProps {
  hasSelection: boolean
  isOpen: boolean
  dropdownLabel: string | undefined
  getToggleButtonProps: (options?: any) => any
  children: React.ReactNode | React.ReactNode[]
}
interface DropdownOptionsType<T> {
  dropdownOptions: {
    embeddedTitle?: ReactNode | null
    options: Array<T> | null
  }[]
}

export enum DropdownContentAlignment {
  LEFT = 'Left',
  RIGHT = 'Right',
}

interface DropdownProps<T> extends DropdownOptionsType<T> {
  headline?: ReactNode
  enableSearch?: boolean
  enableMultiSelect?: boolean
  dropdownLabel?: string
  placeholder?: string
  selection?: T | T[] | null
  onOptionClick: (option: T) => void
  onDisabledClick?: (option: T) => void
  className?: string
  renderDropdownButton?: (config: IDropdownButtonProps) => ReactNode
  dataTestId?: string
  id?: string
  renderOption?: (option: T) => ReactNode
  renderSelection?: (option: T) => ReactNode
  onOpen?: () => void
  onClose?: (option: T) => void
  onCloseWithoutOption?: () => void
  contentAlignment?: DropdownContentAlignment
}

const DropDownPadding = styled.div`
  padding: 0 15px;
`

const RelativeContainer = styled.div`
  position: relative;
`

const StyledLabel = styled.label`
  display: block;
`

const Input = styled.div<{ selected?: boolean }>`
  position: relative;

  > input {
    width: 100%;
    line-height: 46px;
    padding: 0 15px;
    background-color: ${complementaryColor};
    ${fontFamilyPrimary};
    font-size: ${fsXS}px;
    ${fwMediumBold};
    white-space: nowrap;
    text-overflow: ellipsis;
    border: 1px solid ${inactiveColor};
    border-radius: 2px;

    ::placeholder {
      color: ${inactiveColor};
    }
  }

  ${props =>
    props.selected &&
    css`
      > input {
        border: 1px solid ${primaryColor};
      }
    `};
`

const renderDefaultDropdownButton = ({
  hasSelection,
  isOpen,
  dropdownLabel,
  getToggleButtonProps,
  children,
}: IDropdownButtonProps) => {
  return (
    <DropdownButton
      selected={hasSelection}
      isOpen={isOpen}
      label={dropdownLabel}
      {...getToggleButtonProps()}
    >
      {children}
    </DropdownButton>
  )
}

const renderDefaultOptionSelection = (option: BaseDropdownOptionType) => {
  return (
    <SingleLabelOption data-testid={option.label}>
      {option.label}
    </SingleLabelOption>
  )
}

function useStateReducer<T extends BaseDropdownOptionType>(
  multiSelect: boolean,
  onSelect: (option: T, isDisabled?: boolean) => void,
) {
  return useCallback<
    (
      state: DownshiftState<T>,
      changes: StateChangeOptions<T>,
    ) => Partial<StateChangeOptions<T>>
  >(
    (state, changes) => {
      switch (changes.type) {
        case Downshift.stateChangeTypes.keyDownEnter:
        case Downshift.stateChangeTypes.clickItem:
          if (changes.selectedItem) {
            onSelect(changes.selectedItem, changes.selectedItem.disabled)
          }
          return {
            ...changes,
            isOpen: multiSelect ? state.isOpen : changes.isOpen,
            highlightedIndex: multiSelect
              ? state.highlightedIndex
              : changes.highlightedIndex,
          }

        default:
          return changes
      }
    },
    [multiSelect, onSelect],
  )
}

const getContentAlignmentStyles = (
  contentAlignment?: DropdownContentAlignment,
) => ({
  ...(contentAlignment === DropdownContentAlignment.LEFT ? { left: 0 } : {}),
  ...(contentAlignment === DropdownContentAlignment.RIGHT ? { right: 0 } : {}),
})

function Dropdown<T extends BaseDropdownOptionType>({
  headline,
  enableSearch = false,
  enableMultiSelect = false,
  dropdownLabel,
  placeholder = '...',
  dropdownOptions,
  selection,
  onOptionClick,
  onDisabledClick,
  className,
  renderDropdownButton = renderDefaultDropdownButton,
  renderOption = renderDefaultOptionSelection,
  renderSelection = renderDefaultOptionSelection,
  dataTestId,
  onOpen,
  onClose,
  onCloseWithoutOption,
  id,
  contentAlignment = DropdownContentAlignment.LEFT,
}: DropdownProps<T>) {
  const hydrationDone = useHydrationDone()

  const combinedOptions: T[] = dropdownOptions
    .map(sizeRun => {
      return sizeRun.options ? sizeRun.options : []
    })
    .reduce((acc, val) => acc.concat(val), [])

  const onSelect = useCallback(
    (option: T, isDisabled?: boolean) => {
      if (!isDisabled) {
        onOptionClick(option)
      } else if (onDisabledClick) {
        onDisabledClick(option)
      }
    },
    [onOptionClick, onDisabledClick],
  )

  const stateReducer = useStateReducer(enableMultiSelect, onSelect)

  const selectedOption = useMemo(() => {
    if (selection && !Array.isArray(selection)) {
      return combinedOptions.find(option => {
        return option.id && option.id === selection.id
      })
    }
  }, [combinedOptions, selection])

  const selectedOptions = useMemo(() => {
    if (selection && Array.isArray(selection)) {
      return selection
        .map(selectedOption =>
          combinedOptions.find(option => option.id === selectedOption.id),
        )
        .filter(exists)
    }
    return []
  }, [selection, combinedOptions])

  const isOptionSelected = useCallback(
    (id: string) => {
      if (selection && Array.isArray(selection)) {
        return selection
          .map(option => option.id)
          .reduce((acc, val) => acc.concat(val), [] as string[])
          .includes(id)
      }
      return exists(selectedOption) && selectedOption.id === id
    },
    [selection, selectedOption],
  )

  if (combinedOptions.length === 0) {
    return null
  }

  const filterOptions = (inputValue: string | null, value: string) => {
    return !inputValue || value.includes(inputValue)
  }

  const hasSelection = exists(selectedOption) || selectedOptions.length > 0

  if (!hydrationDone) {
    return null
  }

  return (
    <Downshift
      id={id || dataTestId || 'dropdown'}
      stateReducer={stateReducer}
      itemToString={item => (item ? item.id : '')}
      onStateChange={changes => {
        if (typeof changes.isOpen === 'boolean') {
          if (changes.isOpen) {
            onOpen?.()
          } else if (changes.selectedItem) {
            onClose?.(changes.selectedItem)
          } else if (!changes.selectedItem && !changes.isOpen) {
            onCloseWithoutOption?.()
          }
        }
      }}
    >
      {({
        getInputProps,
        getItemProps,
        getLabelProps,
        getRootProps,
        getMenuProps,
        getToggleButtonProps,
        isOpen,
        openMenu,
        inputValue,
        highlightedIndex,
      }) => {
        return (
          <RelativeContainer
            data-testid={dataTestId || 'GenericDropdown'}
            className={className}
            {...getRootProps({ refKey: 'ref' })}
          >
            <StyledLabel {...getLabelProps()}>{headline}</StyledLabel>
            <Input selected={Boolean(selectedOption) || isOpen}>
              {enableSearch ? (
                <input
                  placeholder={placeholder}
                  {...getInputProps({
                    onFocus: openMenu,
                  })}
                />
              ) : (
                renderDropdownButton({
                  hasSelection,
                  isOpen,
                  dropdownLabel,
                  getToggleButtonProps,
                  children: getDropdownButtonContent({
                    enableMultiSelect,
                    placeholder,
                    selectedOptions,
                    selectedOption,
                    renderSelection,
                  }),
                })
              )}
            </Input>
            <DropdownBox
              as="ul"
              data-testid="DropdownList"
              style={getContentAlignmentStyles(contentAlignment)}
              {...getMenuProps()}
            >
              {isOpen ? (
                dropdownOptions.map((sizeRun, index) => {
                  return (
                    <Fragment key={index}>
                      <DropDownPadding data-testid={'EmbeddedDropdownTitle'}>
                        {sizeRun.embeddedTitle}
                      </DropDownPadding>
                      {sizeRun.options &&
                        sizeRun.options
                          .filter(
                            option =>
                              !enableSearch ||
                              filterOptions(inputValue, option.label),
                          )
                          .filter(option => !option.hidden)
                          .map((option, index) => {
                            // onMouseMove is regenerated on each call.
                            // TODO revert as soon as resolved https://github.com/downshift-js/downshift/issues/730
                            const { onMouseMove, ...props } = getItemProps({
                              key: option.id,
                              item: option,
                            })
                            return (
                              <MemoOptionContainer
                                key={option.id}
                                {...props}
                                selected={isOptionSelected(option.id)}
                                disabled={option.disabled}
                                withCheckbox={enableMultiSelect}
                                highlighted={index === highlightedIndex}
                                tooltipLabel={option.tooltipLabel}
                              >
                                {renderOption({
                                  ...option,
                                })}
                              </MemoOptionContainer>
                            )
                          })}
                    </Fragment>
                  )
                })
              ) : (
                <div />
              )}
            </DropdownBox>
          </RelativeContainer>
        )
      }}
    </Downshift>
  )
}
export default Dropdown

function getDropdownButtonContent<T extends BaseDropdownOptionType>({
  placeholder,
  enableMultiSelect,
  selectedOptions,
  selectedOption,
  renderSelection,
}: {
  placeholder: string
  enableMultiSelect: boolean
  selectedOptions: T[]
  selectedOption?: T
  renderSelection: (option: T) => ReactNode
}) {
  if (enableMultiSelect) {
    return (
      <MultiSelectLabel
        labels={selectedOptions.map(option => option.label)}
        placeholder={placeholder}
      />
    )
  } else if (selectedOption && !selectedOption.disabled) {
    return renderSelection(selectedOption)
  } else {
    return placeholder
  }
}
